/**
 * La celda de una persona dentro de una tabla de gente, con su enlace al
 * perfil.
 *
 * POR QUÉ NO ES LA FILA ENTERA LA QUE SE TOCA. La idea era que la fila
 * completa abriera el perfil, pero las filas tienen adentro desplegables y
 * botones —cambiar la categoría, aprobar, quitar—, y un enlace no puede
 * contener un botón: el navegador no sabe cuál de los dos se apretó, el
 * lector de pantalla anuncia una cosa que es dos, y con el teclado se queda
 * atrapado. La celda del nombre SÍ puede ser el enlace, y es la que la gente
 * apunta cuando quiere saber quién es alguien.
 *
 * EL ENLACE OCUPA TODA LA CELDA y no solo las letras. Un enlace del tamaño
 * del texto se falla con el dedo; del tamaño de la celda —foto incluida— no.
 *
 * LO QUE VA DEBAJO DEL NOMBRE lo elige cada tabla, porque cada una está
 * contestando una pregunta distinta: en las solicitudes importa la edad, en
 * Retos los kilómetros, en el club el nombre legal. Lo que NO cambia entre
 * tablas es el orden —foto, apodo, y abajo el resto— para que mirar dos
 * pantallas distintas no obligue a aprender dos formas.
 *
 * `debajo` ES LO QUE SE PUEDE APRETAR, Y VA FUERA DEL ENLACE. Un botón
 * adentro de un enlace no es HTML válido —el navegador no sabe cuál de los
 * dos se apretó, el lector de pantalla anuncia una cosa que es dos, y con el
 * teclado se queda atrapado— así que va debajo, en la misma celda pero como
 * hermano. Es lo que permitió que la tabla de Retos pase de tres columnas a
 * dos: el botón de contactar bajó del costado del nombre a debajo del nombre,
 * y con eso la tabla dejó de arrastrarse de lado en un teléfono.
 */
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import type { ReactNode } from 'react';

import { Avatar } from './Avatar';
import { assetUrl } from '../lib/api';

export function PersonCell({
  userId,
  displayName,
  avatarPath,
  note,
  debajo,
  size = 32,
}: {
  /**
   * A quién abre esta celda. `null` es un INVITADO: alguien que juega y no
   * tiene cuenta, así que no hay ficha adónde ir.
   */
  userId: string | null;
  displayName: string;
  avatarPath: string | null;
  /** El renglón de abajo. Sin él, la celda es solo foto y apodo. */
  note?: ReactNode;
  /** Lo que se puede apretar, debajo del enlace y NUNCA adentro. */
  debajo?: ReactNode;
  size?: number;
}): JSX.Element {
  const { t } = useTranslation();

  const dentro = (
    <>
      <Avatar url={assetUrl(avatarPath)} name={displayName} size={size} />
      <span className="person-text">
        <span className="person-name">{displayName}</span>
        {note !== undefined && note !== '' && <span className="person-note">{note}</span>}
      </span>
    </>
  );

  return (
    <td className="person">
      {/*
        SIN CUENTA NO HAY ENLACE, y no es lo mismo que un enlace apagado: un
        `/jugadores/null` lleva a una pantalla de error con el nombre de una
        persona real encima, y quien lo tocó cree que la aplicación se rompió.
        La celda se dibuja igual —la persona existe, jugó— nada más que no
        lleva a ningún lado.
      */}
      {userId === null ? (
        <span className="person-plain">{dentro}</span>
      ) : (
        <Link to={`/jugadores/${userId}`} aria-label={t('player.open', { name: displayName })}>
          {dentro}
        </Link>
      )}
      {debajo}
    </td>
  );
}
